import React, { useCallback, useContext } from 'react'
import { Topbar } from './topbar'
import { ProjectNavigation } from './ProjectNavigation'
import { Transcripts } from './transcripts'
import { Sidebar } from './sidebar'
import { ProjectContext } from '../../context/ProjectContext'
import { StickyGrid } from './stickyGrid'
import { NotesInput } from './notesInput'
import { Overview } from './overview'
import { OverviewSidebar } from './overview/sidebar'
import { Meetings } from './meetings'
import { FeedbacksIndex } from './feedbacks/index'
import GroupNotesByCategoryIcon from '../icons/GroupNotesByCategory'
import GroupNotesByMeetingIcon from '../icons/GroupNotesByMeeting'
import GroupNotesByTableIcon from '../icons/GroupNotesByTable'
import QAsIndex from './qas'
import QuotesIndex from './quotes'
import ProblemsIndex from './problems'
import { SortFilter } from './overview/filters/sortFilter'
import { StarsFilter } from './overview/filters/stars'
import { CategoriesFilter } from './overview/filters/categories'
import { MeetingsFilter } from './overview/filters/meetings'

export function Project() {
  const {
    isLoading,
    groupNotesBy,
    setGroupNotesBy,
    setScrollTopContainer,
    setSidebarScrollContainer,
    menu,
    project,
    needsFilters,
    setNeedsFilters,
  } = useContext(ProjectContext)

  const setContainerRef = useCallback(
    (node) => {
      if (node) setScrollTopContainer(node)
    },
    [setScrollTopContainer]
  )

  const setSidebarContainerRef = useCallback(
    (node) => {
      if (node) setSidebarScrollContainer(node)
    },
    [setSidebarScrollContainer]
  )

  if (isLoading) {
    return (
      <div className="h-screen bg-grey-100">
        <div className="w-full fixed top-0 bg-white z-10">
          <Topbar />
        </div>
        <div
          className="flex justify-center items-center bg-grey-100"
          style={{ height: 'calc(100vh - 60px)', marginTop: '60px' }}
        >
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="h-screen bg-grey-100">
        <div className="w-full fixed top-0 bg-white z-10">
          <Topbar />
        </div>
        <div className="flex pt-[60px]">
          <div
            className="fixed left-0 top-[60px] bg-white border-r border-r-[#e4e4e4]"
            style={{ height: 'calc(100% - 60px)' }}
          >
            <ProjectNavigation />
          </div>
          <div className="ml-[75px] w-[calc(100%-75px)] pt-[20px]">
            {renderMenuContent(menu, setContainerRef, setSidebarContainerRef, groupNotesBy, setGroupNotesBy)}
          </div>
        </div>
      </div>
    </>
  )
}

function LoadingSpinner() {
  return (
    <svg className="w-12 h-12" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
      <radialGradient id="a1" cx=".66" fx=".66" cy=".3125" fy=".3125" gradientTransform="scale(1.5)">
        <stop offset="0" stopColor="#FF156D"></stop>
        <stop offset=".3" stopColor="#FF156D" stopOpacity=".9"></stop>
        <stop offset=".6" stopColor="#FF156D" stopOpacity=".6"></stop>
        <stop offset=".8" stopColor="#FF156D" stopOpacity=".3"></stop>
        <stop offset="1" stopColor="#FF156D" stopOpacity="0"></stop>
      </radialGradient>
      <circle
        transform-origin="center"
        fill="none"
        stroke="url(#a1)"
        strokeWidth="15"
        strokeLinecap="round"
        strokeDasharray="200 1000"
        strokeDashoffset="0"
        cx="100"
        cy="100"
        r="70"
      >
        <animateTransform
          type="rotate"
          attributeName="transform"
          calcMode="spline"
          dur="2"
          values="360;0"
          keyTimes="0;1"
          keySplines="0 0 1 1"
          repeatCount="indefinite"
        ></animateTransform>
      </circle>
      <circle
        transform-origin="center"
        fill="none"
        opacity=".2"
        stroke="#FF156D"
        strokeWidth="15"
        strokeLinecap="round"
        cx="100"
        cy="100"
        r="70"
      ></circle>
    </svg>
  )
}

function renderMenuContent(menu, setContainerRef, setSidebarContainerRef, groupNotesBy, setGroupNotesBy) {
  switch (menu) {
    case 'overview':
      return (
        <div className="pt-[20px] flex gap-[36px]">
          <div
            className="fixed left-[125px] rounded-t-2xl flex overflow-auto hideScrollbar"
            style={{ height: 'calc(100% - 100px)', width: 'calc(100% - 125px - 50px - 372px - 56px - 36px)' }}
          >
            <Overview />
          </div>
          <aside
            className="h-[331px] fixed right-[50px] p-7 overflow-x-visible overflow-y-auto hideScrollbar"
            style={{ height: 'calc(100% - 100px)' }}
          >
            <OverviewSidebar />
          </aside>
        </div>
      )
    case 'meetings':
      return (
        <div
          className="fixed flex w-[calc(100%-75px)] overflow-auto hideScrollbar"
          style={{ height: 'calc(100% - 80px)' }}
        >
          <Meetings />
        </div>
      )
    case 'feedbacks':
      return (
        <div
          className="fixed flex w-[calc(100%-75px)] overflow-auto hideScrollbar"
          style={{ height: 'calc(100% - 80px)' }}
        >
          <FeedbacksIndex />
        </div>
      )
    case 'qas':
      return (
        <div
          className="fixed flex w-[calc(100%-75px)] overflow-auto hideScrollbar"
          style={{ height: 'calc(100% - 80px)' }}
        >
          <QAsIndex />
        </div>
      )
    case 'quotes':
      return (
        <div
          className="fixed flex w-[calc(100%-75px)] overflow-auto hideScrollbar"
          style={{ height: 'calc(100% - 80px)' }}
        >
          <QuotesIndex />
        </div>
      )
    case 'problems':
      return (
        <div
          className="fixed flex w-[calc(100%-75px)] overflow-auto hideScrollbar"
          style={{ height: 'calc(100% - 80px)' }}
        >
          <ProblemsIndex />
        </div>
      )
    case 'notes':
      return (
        <NotesView
          setSidebarContainerRef={setSidebarContainerRef}
          setContainerRef={setContainerRef}
          groupNotesBy={groupNotesBy}
          setGroupNotesBy={setGroupNotesBy}
        />
      )
    case 'transcripts':
      return <TranscriptsView setSidebarContainerRef={setSidebarContainerRef} setContainerRef={setContainerRef} />
    default:
      return null
  }
}

const TranscriptsView = ({ setSidebarContainerRef, setContainerRef }) => {
  const { meetings, transcripts } = useContext(ProjectContext)
  const hasContent = meetings.length > 0 && transcripts.some((transcript) => transcript.meeting_id)

  return (
    <div className="h-[calc(100vh-80px)] pt-5">
      {hasContent && (
        <aside
          ref={setSidebarContainerRef}
          className="w-[372px] fixed mx-10 p-6 bg-white rounded-t-2xl shadow overflow-auto hideScrollbar mt-[50px]"
          style={{ height: 'calc(100% - 188px)' }}
        >
          <Sidebar transcriptsView={true} />
        </aside>
      )}
      <div
        ref={setContainerRef}
        className={`fixed overflow-auto hideScrollbar ${!hasContent ? 'flex items-center justify-center px-8 mx-8' : ''}`}
        style={{
          height: hasContent ? 'calc(100vh - 110px)' : 'calc(100vh - 120px)',
          width: hasContent ? 'calc(100% - 492px - 75px)' : 'calc(100% - 166px)',
          right: hasContent ? '40px' : '83px',
          left: hasContent ? 'auto' : '83px',
          top: hasContent ? '80px' : '90px',
          display: !hasContent ? 'flex' : 'block',
          alignItems: !hasContent ? 'center' : 'initial',
        }}
      >
        <div className={!hasContent ? 'w-full h-[calc(100vh-200px)]' : ''}>
          <Transcripts />
        </div>
      </div>
    </div>
  )
}

const NotesView = ({ setSidebarContainerRef, setContainerRef, groupNotesBy, setGroupNotesBy }) => {
  const { notes, needsFilters, setNeedsFilters } = useContext(ProjectContext)
  const hasNotes = notes && notes.length > 0

  return (
    <div className="relative h-full">
      <>
        <div className="px-[50px] py-[30px] flex items-center justify-between mb-6">
          <h1 className="text-[#3f424a] text-[26px] font-semibold font-['Manrope'] leading-[29.90px]">Needs</h1>
          <div className="flex items-center gap-3">
            <div className="h-10 rounded-[5px] bg-white shadow border border-grey-200 flex items-center overflow-visible pointer-events-auto">
              <StarsFilter filters={needsFilters} setFilters={setNeedsFilters} label={'Needs'} />
              <CategoriesFilter filters={needsFilters} setFilters={setNeedsFilters} />
              <MeetingsFilter filters={needsFilters} setFilters={setNeedsFilters} />
              <SortFilter filters={needsFilters} setFilters={setNeedsFilters} />
            </div>
            <div
              className="h-10 rounded-[5px] bg-white shadow border border-grey-200 flex items-center flex-shrink-0 z-20"
              style={{ width: '120px' }}
            >
              <div
                className={`mx-2 cursor-pointer ${groupNotesBy === 'meeting' && 'bg-lightPink2'}`}
                onClick={() => {
                  setGroupNotesBy('meeting')
                }}
              >
                <GroupNotesByMeetingIcon groupNotesBy={groupNotesBy} />
              </div>
              <div
                className={`mx-2 cursor-pointer ${groupNotesBy === 'category' && 'bg-lightPink2'}`}
                onClick={() => {
                  setGroupNotesBy('category')
                }}
              >
                <GroupNotesByCategoryIcon groupNotesBy={groupNotesBy} />
              </div>
              <div
                className={`mr-2 cursor-pointer ${groupNotesBy === 'table' && 'bg-lightPink2'}`}
                onClick={() => {
                  setGroupNotesBy('table')
                }}
              >
                <GroupNotesByTableIcon groupNotesBy={groupNotesBy} />
              </div>
            </div>
          </div>
        </div>
        {hasNotes && groupNotesBy !== 'table' && (
          <aside
            ref={setSidebarContainerRef}
            className="w-[372px] fixed mx-10 p-6 bg-white rounded-t-2xl shadow overflow-auto hideScrollbar"
            style={{ height: 'calc(100% - 230px)', top: '180px' }}
          >
            <Sidebar transcriptsView={false} />
          </aside>
        )}
      </>
      <div
        ref={setContainerRef}
        className={`fixed overflow-auto hideScrollbar ${!hasNotes ? 'flex items-center justify-center px-12' : ''}`}
        style={
          !hasNotes
            ? {
                height: 'calc(100vh - 240px)',
                width: 'calc(100% - 115px)',
                right: '40px',
                left: '75px',
                top: '180px',
                zIndex: 10,
              }
            : groupNotesBy === 'table'
              ? {
                  height: 'calc(100% - 300px)',
                  width: 'calc(100% - 80px - 75px)',
                  right: '40px',
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '16px',
                  top: '180px',
                  zIndex: 10,
                }
              : groupNotesBy === 'category'
                ? {
                    height: 'calc(100% - 300px)',
                    width: 'calc(100% - 412px - 75px)',
                    right: 0,
                    paddingBottom: '5px',
                    top: '180px',
                    zIndex: 10,
                  }
                : {
                    height: 'calc(100% - 300px)',
                    width: 'calc(100% - 412px - 75px)',
                    right: '40px',
                    paddingLeft: '80px',
                    top: '180px',
                    zIndex: 10,
                  }
        }
      >
        <StickyGrid groupNotesBy={groupNotesBy} />
      </div>
      {hasNotes && (
        <div
          className="h-24 fixed bottom-0 right-0"
          style={{ width: groupNotesBy === 'table' ? 'calc(100% - 80px)' : 'calc(100% - 372px - 40px)' }}
        >
          <NotesInput />
        </div>
      )}
    </div>
  )
}
